import { Component, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";
import { CompetitorVehicleService } from "../../../services/competitor-vehicle.service";
import { ErrorHandlerDialogComponent } from "../../error-handler-dialog/error-handler-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ExcelExportService } from "../../../services/excel-export.service";
import { ReportConstants } from "../ReportConstants";
import { ReaderService } from "../../../services/reader.service";
import * as _ from "lodash";

interface Menu {
  model: UntypedFormControl;
  availableOptions: [];
  selectedOptions: any[] | string;
}

@Component({
    selector: "app-competitor-vehicle",
    templateUrl: "./competitor-vehicle.component.html",
    styleUrls: ["./competitor-vehicle.component.scss"],
    standalone: false
})
export class CompetitorVehicleComponent implements OnInit {
  columnList: any[] = [];
  segmentList: any[] = [];
  constants: ReportConstants = new ReportConstants();
  defaultRegion: any = [];

  changedYear = false;
  changedSegment = false;
  changedRegion = false;

  year: Menu = {
    model: new FormControl(undefined, [Validators.required]),
    availableOptions: [],
    selectedOptions: "",
  };

  segments: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  regions: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  formGroup = new FormGroup({
    year: this.year.model,
    segments: this.segments.model,
    regions: this.regions.model,
  });

  isLoading = false;
  displayTable = false;

  constructor(
    public readonly dialog: MatDialog,
    private readonly competitorVehicleService: CompetitorVehicleService,
    private readonly excelExport: ExcelExportService,
    private readonly readerService: ReaderService
  ) {}

  ngOnInit(): void {
    this.beginLoading();
    this.competitorVehicleService
      .getCompetitorVehicleReportMenuOptions()
      .subscribe({
        next: (x: any) => {
          this.handleGlobalSegmentMenuItems(x);
        },
        error: (err: any) => {
          this.endLoading();
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: { message: err.message },
          });
        },
        complete: () => {
          this.subscribeToFormControls();
          this.selectAllMenuOptions();
          this.formGroup.updateValueAndValidity();
          this.endLoading();
        },
      });
  }

  public subscribeToFormControls(): void {
    this.formGroup.controls["year"].valueChanges.subscribe((value) => {
      this.onChangeYear(value);
    });
    this.formGroup.controls["segments"].valueChanges.subscribe((value) => {
      this.onChangeSegment(value);
    });
    this.formGroup.controls["regions"].valueChanges.subscribe((value) => {
      this.onChangeRegion(value);
    });
  }

  public onChangeSegment(newValue): void {
    this.segments.selectedOptions = newValue;
    if (newValue != null && this.segments.selectedOptions.length > 0) {
      this.readerService
        .getRegionForSegments(this.segments.selectedOptions as string)
        .subscribe({
          next: (res) => {
            res.pop(); //Remove ELC
            this.regions.selectedOptions = _.filter(
              this.regions.selectedOptions,
              (x) => {
                return res.includes(x);
              }
            );
            this.formGroup.controls["regions"].setValue(
              this.regions.selectedOptions,
              { onlySelf: true, emitEvent: false }
            );
            this.regions.availableOptions = res;
          },
          error: (res) => {},
          complete: () => {},
        });
    }
  }

  public onChangeRegion(newValue): void {
    this.regions.selectedOptions = newValue;
  }

  public onChangeYear(newValue): void {
    this.year.selectedOptions = newValue;
  }

  public generateReport(): void {
    if (!this.verifySearchCriteria()) {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: { message: "Please specify search criteria." },
      });
    } else {
      this.segmentList = [];
      this.displayTable = false;
      this.beginLoading();
      this.competitorVehicleService
        .getCompetitorVehicleReportData(
          this.year.selectedOptions,
          this.segments.selectedOptions,
          this.regions.selectedOptions
        )
        .subscribe({
          next: (x: any) => {
            this.handleCompetitorVehicleReportData(x);
          },
          error: (err: any) => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: { message: err.message },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    }
  }

  public exportToExcel(): void {
    if (!this.verifySearchCriteria()) {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: { message: "Please specify search criteria." },
      });
    } else {
      this.beginLoading();
      this.competitorVehicleService
        .exportToExcel(
          this.year.selectedOptions,
          this.segments.selectedOptions,
          this.regions.selectedOptions
        )
        .subscribe({
          next: (response: any) => {
            this.excelExport.exportToExcelFromServer(
              response,
              "CompetitorVehicleReport"
            );
          },
          error: () => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: { message: "Error occurred while creating excel file." },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    }
  }

  public clearFilters(): void {
    this.segmentList = [];
    this.regions.selectedOptions = [];
    this.formGroup.controls["regions"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.segments.selectedOptions = [];
    this.formGroup.controls["segments"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.year.selectedOptions = "";
    this.formGroup.controls["year"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });
    this.formGroup.markAsUntouched();
    this.formGroup.reset();
  }

  public handleGlobalSegmentMenuItems(data: any): void {
    this.segments.availableOptions = data.segmentMenuItems;
    data.regionMenuItems.pop(); //Remove ELC
    this.regions.availableOptions = data.regionMenuItems;
    this.year.availableOptions = data.yearsToDisplay;
    this.defaultRegion = data.regionMenuItems;
  }

  public handleCompetitorVehicleReportData(data: {
    globalSegmentList: string | any[];
    columnNames: any[];
  }): void {
    if (data.globalSegmentList && data.globalSegmentList.length > 0) {
      this.segmentList = data.globalSegmentList as any[];
      this.columnList = data.columnNames;
      this.displayTable = true;
    } else {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: {
          message:
            "There are no entries matching the selected report criteria.",
        },
      });
    }
  }

  public verifySearchCriteria(): boolean {
    return this.formGroup.valid;
  }

  public beginLoading(): void {
    this.isLoading = true;
  }

  public endLoading(): void {
    this.isLoading = false;
  }

  public selectAllMenuOptions(): void {
    const date = new Date();
    this.year.selectedOptions = String(date.getFullYear());
    this.formGroup.controls["year"].setValue(this.year.selectedOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.regions.availableOptions = this.defaultRegion;
    this.regions.selectedOptions = this.regions.availableOptions;
    this.formGroup.controls["regions"].setValue(this.regions.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.segments.selectedOptions = this.segments.availableOptions;
    this.formGroup.controls["segments"].setValue(
      this.segments.availableOptions,
      { onlySelf: true, emitEvent: false }
    );

    this.formGroup.markAllAsTouched();
  }
}
